import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Truck, LogOut } from "lucide-react";
import { getDrivers, getOffers, getSettings } from "@/lib/api";
import { playAlert, playDing } from "@/lib/sound";
import Header from "@/components/Header";
import DriverCard from "@/components/DriverCard";
import FeedPanel from "@/components/FeedPanel";
import EmailPanel from "@/components/EmailPanel";
import { WhatsAppPanel } from "@/components/WhatsAppPanel";
import { Login } from "@/components/Login";

function Dashboard({ onLogout }) {
  const seenMatchIds = useRef(null);
  const seenUnread = useRef(null);

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: getDrivers,
    refetchInterval: 5000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: getSettings,
  });
  const { data: offers = [] } = useQuery({
    queryKey: ["offers"],
    queryFn: () => getOffers(60),
    refetchInterval: 3000,
  });

  // Detect newly matched offers -> loud PC sound + toast
  useEffect(() => {
    const matched = offers.filter((o) => o.matched);
    const ids = new Set(matched.map((o) => o.id));
    if (seenMatchIds.current === null) {
      seenMatchIds.current = ids;
      return;
    }
    const fresh = matched.filter((o) => !seenMatchIds.current.has(o.id));
    if (fresh.length > 0) {
      playAlert();
      fresh.forEach((o) => {
        const top = o.matches[0];
        toast.success(`Cursă nouă pt. ${top.driver_label}`, {
          description: `[${o.source}] Cod Poștal ${o.extracted.load_postal} → ${
            o.extracted.pallets ?? "?"
          } Paleți / ${o.extracted.weight_kg ?? "?"} kg`,
          duration: 12000,
        });
      });
    }
    seenMatchIds.current = ids;
  }, [offers]);

  // Detect new client replies in offer chats -> short ding + toast
  useEffect(() => {
    const byKey = {};
    offers.forEach((o) => {
      if (o.conv_key) byKey[o.conv_key] = { n: o.unread || 0, o };
    });
    if (seenUnread.current === null) {
      seenUnread.current = byKey;
      return;
    }
    const fresh = Object.entries(byKey).filter(([k, v]) => v.n > (seenUnread.current[k]?.n || 0));
    if (fresh.length > 0) {
      playDing();
      fresh.forEach(([, v]) => {
        const who = (v.o.sender || v.o.email_from || "client").replace(/<[^>]*>/, "").trim();
        toast.info(`Mesaj nou de la ${who}`, { duration: 8000 });
      });
    }
    seenUnread.current = byKey;
  }, [offers]);

  const freeCount = drivers.filter((d) => d.status === "free").length;
  const matchCount = offers.filter((o) => o.matched).length;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Header freeCount={freeCount} matchCount={matchCount} />
      <button
        onClick={onLogout}
        data-testid="logout-button"
        className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-600 text-slate-300 text-sm font-semibold hover:bg-red-900/60 hover:text-red-200 hover:border-red-700 transition-colors backdrop-blur"
      >
        <LogOut className="w-4 h-4" /> Ieșire
      </button>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 p-4 sm:p-6 max-w-[1600px] mx-auto">
        {/* Fleet */}
        <section className="xl:col-span-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-600/20 text-blue-400">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight">
                Panou Dispecerat Dube
              </h2>
              <p className="text-slate-400 text-sm">
                Cei 8 șoferi · setează locația, capacitatea și statusul
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="drivers-grid">
            {drivers.map((d) => (
              <DriverCard key={d.id} driver={d} />
            ))}
          </div>
        </section>

        {/* Sidebar */}
        <aside className="xl:col-span-4 space-y-5">
          <WhatsAppPanel />
          <EmailPanel />
          <FeedPanel offers={offers} radius={settings?.matching_radius_km} />
        </aside>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  const [authed, setAuthed] = useState(() => localStorage.getItem("cls_auth") === "1");

  const logout = () => {
    localStorage.removeItem("cls_auth");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="App">
        <Login onLogin={() => setAuthed(true)} />
      </div>
    );
  }

  return (
    <div className="App">
      <Dashboard onLogout={logout} />
    </div>
  );
}

export default App;
